import React from 'react';
import { AutoCompleteProps, GlobalToken, Typography } from 'antd';

const { Text } = Typography;

interface FormattedLabelProps {
	text: string;
	matches: google.maps.places.PredictionSubstring[];
	token: GlobalToken;
}

const FormattedLabel: React.FC<FormattedLabelProps> = ({ text, matches, token }) => {
	if (matches.length === 0) {
		return (
			<Text
				style={{
					fontSize: token.fontSizeSM,
					color: token.colorTextSecondary,
					lineHeight: token.lineHeight,
				}}
			>
				{text}
			</Text>
		);
	}

	const segments: { text: string; highlighted: boolean }[] = [];
	let lastIndex = 0;

	matches
		.sort((a, b) => a.offset - b.offset)
		.forEach(match => {
			if (match.offset > lastIndex) {
				segments.push({
					text: text.substring(lastIndex, match.offset),
					highlighted: false,
				});
			}

			segments.push({
				text: text.substring(match.offset, match.offset + match.length),
				highlighted: true,
			});

			lastIndex = match.offset + match.length;
		});

	if (lastIndex < text.length) {
		segments.push({
			text: text.substring(lastIndex),
			highlighted: false,
		});
	}

	return (
		<Text>
			{segments.map((segment, index) =>
				segment.highlighted ? (
					<span key={index} style={{ fontWeight: token.fontWeightStrong }}>
						{segment.text}
					</span>
				) : (
					<span
						key={index}
						style={{
							fontSize: token.fontSizeSM,
							color: token.colorTextSecondary,
							lineHeight: token.lineHeight,
						}}
					>
						{segment.text}
					</span>
				)
			)}
		</Text>
	);
};

export const getPredictions = async (
	input: string,
	country: string,
	onComplete: (options: AutoCompleteProps['options']) => void,
	token: GlobalToken,
	autoCompleteService?: google.maps.places.AutocompleteService,
	sessionToken?: google.maps.places.AutocompleteSessionToken,
	preventAutoComplete = false
) => {
	if (input.length < 3 || !autoCompleteService || preventAutoComplete) {
		onComplete([]);
		return;
	}

	await autoCompleteService.getPlacePredictions(
		{ input, sessionToken, componentRestrictions: { country } },
		predictions => {
			onComplete(
				(predictions ?? []).map(prediction => {
					return {
						value: prediction.place_id,
						label: (
							<FormattedLabel
								text={prediction.description}
								matches={prediction.matched_substrings || []}
								token={token}
							/>
						),
					};
				})
			);
		}
	);
};
